// Phase 15 — Distributor Portal shell (mobile-first, bottom-nav).
// Finance tab removed — a distributor's own read-only Claims/My Payments
// view was the only thing there and isn't part of this portal anymore.
// Beat tab added — lets a distributor punch a secondary order (or mark an
// outlet non-productive) directly against their own beats, the same job
// TodayBeat.jsx does for SE/ASM in the field app.
import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Package, Truck, LogOut, MapPin, Home, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TulsiLogo } from "@/lib/brand";
import NotificationBell from "@/components/NotificationBell";
import ShellBackButton from "@/components/ShellBackButton";
import BroadcastPopup, { BROADCASTS_CHANGED_EVENT, BROADCAST_CHECK_EVENT } from "@/components/BroadcastPopup";

// Labels kept short on purpose — 5 columns on a ~360px phone leaves each
// tab only ~72px, and a longer word (the old "Fulfillment") overflowed its
// column and visually ran into its neighbors' labels.
const NAV = [
  { to: "/portal/home",          label: "Home",     icon: Home,         testid: "portal-nav-home", end: true },
  { to: "/portal/beat",          label: "Beat",     icon: MapPin,       testid: "portal-nav-beat" },
  { to: "/portal/orders",        label: "Orders",   icon: ShoppingCart, testid: "portal-nav-orders" },
  { to: "/portal/stock",         label: "Stock",    icon: Package,      testid: "portal-nav-stock" },
  { to: "/portal/primary-orders",label: "Primary",  icon: Truck,        testid: "portal-nav-primary" },
];

export default function DistributorShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "?")
    .split(" ").map(n => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  // Unread office broadcast messages — badge on the header Messages button
  // (the bottom nav is full, and the bell only lists the latest 20 alerts).
  const [unreadMessages, setUnreadMessages] = useState(0);
  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/broadcasts/inbox", { params: { page_size: 1 }, skipAuthRedirect: true });
      setUnreadMessages(data?.unread || 0);
    } catch (_e) { /* badge is optional — stay quiet */ }
  }, []);
  useEffect(() => {
    loadUnread();
    window.addEventListener(BROADCASTS_CHANGED_EVENT, loadUnread);
    window.addEventListener(BROADCAST_CHECK_EVENT, loadUnread);
    return () => {
      window.removeEventListener(BROADCASTS_CHANGED_EVENT, loadUnread);
      window.removeEventListener(BROADCAST_CHECK_EVENT, loadUnread);
    };
  }, [loadUnread]);

  return (
    <div className="min-h-screen w-full bg-[#f7f5f9] flex justify-center" data-testid="distributor-shell">
      <div className="w-full max-w-[480px] min-h-screen bg-white flex flex-col shadow-[0_0_0_1px_rgba(0,0,0,0.04)] relative">
        <header className="px-4 pt-4 pb-3 border-b border-neutral-100 flex items-center justify-between gap-2 sticky top-0 bg-white/95 backdrop-blur z-20">
          <div className="flex items-center gap-2 min-w-0">
            <ShellBackButton homePath="/portal/home" />
            <TulsiLogo size={30} />
            <div className="leading-tight min-w-0">
              <div className="text-[13px] font-semibold text-neutral-900 truncate" data-testid="distributor-name">
                {user?.name || "Distributor"}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 truncate">Distributor Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate("/messages")}
              data-testid="portal-messages"
              className="relative h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center"
              aria-label={unreadMessages > 0 ? `Messages, ${unreadMessages} unread` : "Messages"}
            >
              <Mail className="w-4 h-4 text-neutral-700" />
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-[#E5197F] flex items-center justify-center"
                      data-testid="portal-messages-badge">
                  {unreadMessages > 99 ? "99+" : unreadMessages}
                </span>
              )}
            </button>
            <NotificationBell />
            <button
              onClick={() => navigate("/logout")}
              data-testid="portal-logout"
              className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4 text-neutral-700" />
            </button>
            <div className="h-9 w-9 rounded-full tulsi-gradient text-white flex items-center justify-center text-[11px] font-bold">
              {initials}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-24" data-testid="portal-main">
          {children}
        </main>

        {/* `fixed` at every width — see MobileShell.jsx for why `md:absolute`
            is wrong here (it anchors to this shell's own unbounded height
            instead of staying pinned to the viewport on a long page). */}
        <nav className="fixed bottom-0 inset-x-0 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[480px] bg-white border-t border-neutral-200 pb-safe z-30"
             data-testid="portal-bottom-nav">
          {/* NAV is always exactly 5 items (Home, Beat, Fulfillment, Stock,
              Primary) — grid-cols-5 must stay a literal Tailwind class, same
              note as MobileShell.jsx's field bottom nav (JIT can't resolve an
              interpolated class name). Update this literal if the tab count
              ever changes. */}
          <div className="grid h-16 grid-cols-5">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                    isActive ? "text-[#E5197F]" : "text-neutral-500 hover:text-neutral-800"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {isActive && <span className="absolute -bottom-0 h-0.5 w-8 rounded-full tulsi-gradient" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      {/* unread admin broadcast messages — popup until "OK, I have read it" */}
      <BroadcastPopup />
    </div>
  );
}
